import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useToast } from '@bonusly/design-system';

import { useDeleteEvaluation } from '../../hooks/use-delete-evaluation';
import { QueryKey } from '../../types/query-key';

export interface DeleteDraftFeedbackModalProps {
	onCloseGiveModal?: VoidFunction;
	feedbackId?: string;
	isOpen: boolean;
	onClose: VoidFunction;
}

export const DeleteDraftFeedbackModal: FC<DeleteDraftFeedbackModalProps> = ({ onCloseGiveModal, feedbackId, isOpen, onClose }) => {
	const { t } = useTranslation('feedback');
	const toast = useToast();
	const queryClient = useQueryClient();

	const { mutate: deleteFeedback } = useDeleteEvaluation({
		onError: () => {
			toast({
				position: 'bottom-right',
				status: 'error',
				title: t('feedback.draft.delete_feedback_error'),
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries([QueryKey.ConsolidatedFeedbackIndex]);
			toast({
				position: 'bottom-right',
				status: 'success',
				title: t('feedback.draft.delete_feedback_success'),
			});
			onCloseGiveModal?.();
			onClose();
		},
	});

	if (!feedbackId) return null;

	const handleDeleteConfirmClick = () => deleteFeedback(feedbackId);

	return (
		<Modal isCentered autoFocus={false} isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="xs">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader borderBottom="1px solid" borderColor="gray.300">
					{t('feedback.draft.delete_confirm_header')}
				</ModalHeader>
				<ModalCloseButton borderRadius="full" top={4} />
				<ModalBody>
					<Text>{t('feedback.draft.delete_confirm_text')}</Text>
				</ModalBody>

				<ModalFooter px={5} pb={5}>
					<Button colorScheme="primary" mr={3} variant="solid" onClick={onClose} w="50%">
						{t('feedback.draft.cancel_button')}
					</Button>
					<Button colorScheme="secondary" variant="alpha" onClick={handleDeleteConfirmClick} w="50%">
						{t('feedback.draft.delete_button')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
